/*
 * Sticky buy bar for product pages.
 *
 * Design notes:
 * - The real product form owns variant, quantity and property values. The bar
 *   tracks confirmation of option groups, returning to incomplete choices and
 *   submitting that same form once its required configuration is ready.
 * - Price and sold-out state are mirrored from the main product block with a
 *   MutationObserver rather than by subscribing to theme events, so this keeps
 *   working even if Dawn changes how it publishes variant changes.
 * - It shows only once the real add-to-cart button leaves the viewport, so it
 *   never competes with the button the shopper is already looking at.
 */
// A section can load this asset more than once on the same page.
(() => {
  if (customElements.get('sticky-add-to-cart')) return;

  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-submit]');
      this.priceTarget = this.querySelector('[data-sticky-price]');
      this.buttonPriceTarget = this.querySelector('[data-sticky-button-price]');
      this.compareTarget = this.querySelector('[data-sticky-compare]');
      this.variantTarget = this.querySelector('[data-sticky-variant]');
      this.labelTarget = this.querySelector('[data-sticky-label]');
      this.defaultLabel ??= this.labelTarget?.textContent.trim();

      if (!this.button) return;

      /*
       * Scope everything to the main product section.
       *
       * Product cards render their own /cart/add forms for quick add, so a
       * document-wide query would be a coin flip decided by section order — and
       * would silently bind to a related product if the merchant moved sections
       * around or removed the price block.
       */
      this.trackHeaderOffset();

      this.main = document.querySelector('[id^="MainProduct-"]');
      if (!this.main) return this.warn('no [id^="MainProduct-"] section found');

      /*
       * Target the form inside <product-form>. A product page carries two
       * /cart/add forms — the Shop Pay installment form comes first in the DOM
       * and has no add button, so matching on the action alone finds the wrong
       * one and the bar silently never works.
       */
      this.form = this.main.querySelector('product-form form[action*="/cart/add"]');
      this.mainButton = this.form ? this.form.querySelector('[name="add"]') : null;

      if (!this.form || !this.mainButton) {
        return this.warn('could not find product-form > form[action*="/cart/add"] with a [name="add"] button');
      }

      /*
       * getAttribute, not .id: the form contains <input name="id"> for the
       * variant, and HTMLFormElement exposes named controls as properties, so
       * form.id returns that input element rather than the form's id string.
       */
      const formId = this.form.getAttribute('id');

      if (formId) this.button.setAttribute('form', formId);
      this.confirmedOptions ||= new Map();
      const linkedVariant = new URL(window.location.href).searchParams.get('variant');
      this.initialVariantLink ??= !!linkedVariant && linkedVariant === this.form.elements.namedItem('id')?.value;
      this.onChoice ||= (event) => {
        this.main = document.querySelector('[id^="MainProduct-"]') || this.main;
        if (!this.main?.contains(event.target)) return;
        const group = event.target.closest('variant-selects .product-form__input');
        if (!group || (event.type === 'click' && event.target.tagName !== 'INPUT')) return;
        this.checkProductIdentity();
        const groups = this.optionGroups();
        this.confirmedOptions.set(groups.indexOf(group), this.optionValue(group));
      };
      document.addEventListener('change', this.onChoice, true);
      document.addEventListener('click', this.onChoice, true);
      this.onPurchase ||= (event) => {
        event.preventDefault();
        this.syncState();
        if (this.button.disabled) return;
        const missing = this.firstMissingChoice();
        if (missing) {
          this.returnToChoice(missing);
          return;
        }
        // Submit the original form once, preserving variant, quantity and properties.
        if (this.form.requestSubmit) this.form.requestSubmit(this.mainButton);
        else this.mainButton.click();
      };
      this.button.addEventListener('click', this.onPurchase);
      this.checkProductIdentity();
      if (this.initialVariantLink) {
        this.optionGroups().forEach((group, index) => this.confirmedOptions.set(index, this.optionValue(group)));
        this.initialVariantLink = false;
      }

      this.watchVisibility();
      this.watchState();
      this.syncState();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.stateObserver) this.stateObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onHeaderResize) window.removeEventListener('resize', this.onHeaderResize);
      if (this.headerObserver) this.headerObserver.disconnect();
      if (this.onHeaderResize) window.removeEventListener('scroll', this.onHeaderResize);
      this.button?.removeEventListener('click', this.onPurchase);
      if (this.onChoice) {
        document.removeEventListener('change', this.onChoice, true);
        document.removeEventListener('click', this.onChoice, true);
      }
    }

    optionGroups() {
      return Array.from(this.main.querySelectorAll('variant-selects .product-form__input'));
    }

    optionValue(group) {
      return group.querySelector('input:checked')?.value || group.querySelector('select')?.value || '';
    }

    checkProductIdentity() {
      const identity = this.main.querySelector('variant-selects')?.dataset.productId || this.main.id;
      if (this.productIdentity && this.productIdentity !== identity) this.confirmedOptions.clear();
      this.productIdentity = identity;
    }

    firstMissingChoice() {
      this.checkProductIdentity();
      const groups = this.optionGroups();
      for (const [index, group] of groups.entries()) {
        const choices = group.querySelectorAll('input[type="radio"], select option');
        if (group.hidden || choices.length <= 1) continue;
        const value = this.optionValue(group);
        if (!value || this.confirmedOptions.get(index) !== value) {
          return group.querySelector('input:not(:disabled), select:not(:disabled)') || group;
        }
      }
      // form.elements includes fields associated using form= outside the form.
      return Array.from(this.form.elements).find(control => control.willValidate && !control.validity.valid) || null;
    }

    returnToChoice(control) {
      for (let parent = control.parentElement; parent && parent !== this.main; parent = parent.parentElement) {
        if (parent.tagName === 'DETAILS') parent.open = true;
      }
      const target = control.closest('.product-form__input, .product-field') || control;
      const header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
      const offset = Math.max(0, header?.getBoundingClientRect().bottom || 0) + 24;
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset), behavior: reduced ? 'auto' : 'smooth' });
      if (typeof control.focus === 'function') control.focus({ preventScroll: true });
    }

    /*
     * Sit under the header, not over it.
     *
     * Only relevant to the top position: at the bottom of the screen there is
     * nothing to clear. The header's height is measured rather than taken from
     * a setting, because it depends on the logo, the menu and whatever the
     * announcement bar is showing — and it changes when a countdown or a marquee
     * finishes laying out.
     *
     * A header that is not sticky scrolls away, so its offset is only applied
     * while it is actually pinned; otherwise the bar would float below an empty
     * strip.
     */
    trackHeaderOffset() {
      if (!this.classList.contains('sticky-atc--top')) return;

      const header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
      if (!header) return;

      const apply = () => {
        const pinned = getComputedStyle(header).position === 'fixed' || header.tagName === 'STICKY-HEADER';
        const offset = pinned ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
        this.style.setProperty('--sticky-atc-top-offset', `${offset}px`);
      };

      apply();

      this.onHeaderResize = apply;
      window.addEventListener('resize', this.onHeaderResize);

      // The header group changes height after load — fonts, a countdown that
      // renders once it has a value, a marquee that clones until it is wide
      // enough. Watching it covers all of those without naming any.
      if ('ResizeObserver' in window) {
        this.headerObserver = new ResizeObserver(apply);
        this.headerObserver.observe(header);
      }

      // A header that hides on scroll down moves without resizing.
      window.addEventListener('scroll', apply, { passive: true });
    }

    /* Reveal the bar only when the real button is off screen. */
    watchVisibility() {
      // The hidden attribute is only there to cover the moment before this runs.
      // From now on the slide is driven by a class so it can animate.
      this.hidden = false;

      // Start explicitly hidden. The observer fires soon after, but until it does
      // the bar must already be announced as hidden rather than unlabelled.
      this.setVisible(false);

      if (!('IntersectionObserver' in window)) {
        this.setVisible(true);
        return;
      }

      /*
       * "Not intersecting" is true in two different situations: the button has
       * been scrolled past, and the button has not been reached yet. Only the
       * first should reveal the bar.
       *
       * On a tall desktop page the real button is usually on screen at load, so
       * the difference rarely shows. On a phone it starts below the fold, and
       * treating that as "scrolled past" makes the bar appear immediately —
       * duplicating a button the shopper has not even seen yet.
       *
       * boundingClientRect.top separates the two: negative means the button is
       * above the viewport and genuinely behind them.
       */
      /*
       * The merchant can also hold the bar back until the page has been scrolled
       * a set distance, so it does not appear the instant a short product page is
       * nudged. Both conditions have to be true: past the button, and past the
       * distance.
       */
      this.revealOffset = parseInt(this.dataset.revealOffset, 10) || 0;
      this.scrolledPast = false;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.updateVisibility();
        },
        { rootMargin: '0px 0px -80px 0px' }
      );

      if (this.revealOffset > 0) {
        this.onScroll = () => this.updateVisibility();
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      this.observer.observe(this.mainButton);
    }

    updateVisibility() {
      const farEnough = window.scrollY >= this.revealOffset;
      this.setVisible(this.scrolledPast && farEnough);
    }

    setVisible(visible) {
      this.classList.toggle('is-shown', visible);
      this.inert = !visible;
      // Mirrors visibility for assistive tech, which does not read the class.
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    /* Mirror price and availability from the main product block. */
    watchState() {
      // Scoped to the main section for the same reason as the form above.
      this.mainPrice = this.main.querySelector('.price');
      this.stateObserver = new MutationObserver(() => this.syncState());
      // Variant rendering can replace the price and picker nodes themselves.
      this.stateObserver.observe(this.main, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'data-preorder', 'data-atc-price', 'class'],
      });
    }

    syncState() {
      const currentMain = document.querySelector('[id^="MainProduct-"]');
      if (currentMain) this.main = currentMain;
      const currentForm = this.main.querySelector('product-form form[action*="/cart/add"]');
      const currentButton = currentForm?.querySelector('[name="add"]');
      if (currentForm && currentButton) {
        this.form = currentForm;
        this.mainButton = currentButton;
        if (this.form.getAttribute('id')) this.button.setAttribute('form', this.form.getAttribute('id'));
      }
      this.mainPrice = this.main.querySelector('.price');
      if (this.mainPrice && this.priceTarget) {
        /*
         * Scoped to the branch that is actually showing. Dawn renders both a
         * .price__regular and a .price__sale and hides one, and .price__regular
         * comes first in the markup — so an unscoped querySelector for
         * .price-item--regular returns the hidden copy holding the current price,
         * not the struck-through original. That put the same figure in both slots:
         * "$785.95 $785.95", one of them crossed out.
         */
        const onSale = this.mainPrice.classList.contains('price--on-sale');
        const priceSelector = onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
        const source = this.mainPrice.querySelector(priceSelector);
        if (source) {
          const text = source.textContent.trim();
          if (text) this.priceTarget.textContent = text;
        }

        /*
         * Re-checked on every sync, not just once: a shopper moving from a reduced
         * variant to a full-price one would otherwise be left looking at a
         * discount that no longer applies.
         */
        if (this.compareTarget) {
          const compare = onSale ? this.mainPrice.querySelector('.price__sale .price-item--regular') : null;
          const compareText = compare ? compare.textContent.trim() : '';
          this.compareTarget.hidden = !compareText;
          if (compareText) this.compareTarget.textContent = compareText;
        }
        this.priceTarget.classList.toggle('sticky-atc__price--sale', onSale);
      }

      /*
       * The chosen variant, so the bar says which one it is about to add. Read
       * from the picker's own label rather than tracked here, for the same reason
       * the button is wired to the real form: one source of truth.
       */
      if (this.variantTarget) {
        /*
         * Keyed by option name rather than collecting every match. Dawn swaps the
         * whole product-info section on a variant change, and this observer fires
         * mid-swap — with the outgoing and incoming pickers both briefly in the
         * document, a plain collection read the same option twice and the bar said
         * "Sunset / Sunset".
         *
         * Not a plain de-duplicate: two different options can legitimately share a
         * value, and collapsing those would be wrong. Keying on the option name
         * keeps one entry per option however many copies of it exist.
         */
        const chosen = new Map();
        this.main.querySelectorAll('.product-form__input').forEach((field) => {
          const value = field.querySelector('[data-selected-value]');
          const legend = field.querySelector('legend, .form__label');
          if (!value) return;
          const text = value.textContent.trim();
          if (!text) return;
          // The legend holds "Colour: Sunset"; everything before the colon names it.
          const selected = field.querySelector('input:checked, option:checked');
          const name = selected?.dataset.optionName || (legend ? legend.textContent.split(':')[0].trim() : field.className);
          chosen.set(name, text);
        });

        const label = Array.from(chosen.values()).join(' / ');
        this.variantTarget.hidden = !label;
        this.variantTarget.textContent = label;
      }

      const disabled = this.mainButton.hasAttribute('disabled');
      const busy = this.mainButton.getAttribute('aria-disabled') === 'true';
      if (this.buttonPriceTarget) {
        const price = this.mainButton.getAttribute('data-atc-price') || this.priceTarget?.textContent.trim() || '';
        this.buttonPriceTarget.textContent = price;
        this.buttonPriceTarget.hidden = disabled || !price;
      }
      this.button.disabled = disabled || busy;
      this.button.setAttribute('aria-busy', String(busy));

      /*
       * The bar carries the merchant's own label until the real button says
       * something the merchant did not choose — sold out, or a pre-order. Copying
       * it only when disabled left the bar offering "Add to cart" beside a button
       * that said "Pre-order".
       */
      const mirrorLabel = disabled || this.mainButton.hasAttribute('data-preorder');
      if (mirrorLabel && this.labelTarget) {
        const mainLabel = (this.mainButton.querySelector('span') || this.mainButton).textContent.trim();
        if (mainLabel) this.labelTarget.textContent = mainLabel;
      } else if (this.labelTarget && this.defaultLabel) {
        this.labelTarget.textContent = this.defaultLabel;
      }
    }

    /*
     * Dev-only diagnostic.
     *
     * These components hook into Dawn's markup, and Dawn's class names and ids
     * change between versions. Without this, a mismatch just makes the feature
     * quietly stop working — which is far harder to spot than an error. The
     * warning is limited to the theme editor so shoppers never see console noise.
     */
    warn(message) {
      if (window.Shopify && window.Shopify.designMode) {
        console.warn(`[${this.tagName.toLowerCase()}] ${message}`);
      }
    }
  }

  if (!customElements.get('sticky-add-to-cart')) {
    customElements.define('sticky-add-to-cart', StickyAddToCart);
  }
})();
